import {
  FinanceFundListKind,
  FinanceFundListStatus,
  type FinanceCategoryScope,
  type ListFinanceFundsQuery,
} from "@/features/finance/model/finance-types";
import type { DataTableQueryParams } from "@ibada/ui/components/data-table";

/** Sort fields accepted by the funds endpoint (funds.schemas.ts). */
const ALLOWED_SORT_FIELDS: readonly string[] = ["createdAt", "updatedAt", "name"];
const DEFAULT_SORT_BY = "name";

type FundsSortBy = NonNullable<ListFinanceFundsQuery["sortBy"]>;

function parseSerializedFilters(value: string | undefined): Record<string, unknown> {
  if (!value?.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }
  return {};
}

/**
 * An empty/unknown status means "no status filter", which this endpoint spells `ALL`
 * rather than by omitting the param.
 */
function parseStatus(value: unknown): FinanceFundListStatus {
  if (typeof value !== "string" || !value) return FinanceFundListStatus.All;
  const known = Object.values(FinanceFundListStatus) as string[];
  return known.includes(value) ? (value as FinanceFundListStatus) : FinanceFundListStatus.All;
}

function parseSortBy(value: string | undefined): FundsSortBy {
  return (value && ALLOWED_SORT_FIELDS.includes(value) ? value : DEFAULT_SORT_BY) as FundsSortBy;
}

/** Translate shared table query state into the funds list contract. */
export function toFundsListQuery({
  queryParams,
  workspaceId,
  branchId,
  scope,
  kind = FinanceFundListKind.All,
}: {
  queryParams: DataTableQueryParams;
  workspaceId: string;
  branchId?: string | null;
  scope: FinanceCategoryScope;
  /** Toolbar tab. `All` is omitted from the request rather than sent, so it cannot narrow anything. */
  kind?: FinanceFundListKind;
}): ListFinanceFundsQuery {
  return {
    workspaceId,
    branchId,
    scope,
    status: parseStatus(parseSerializedFilters(queryParams.filters).status),
    ...(kind !== FinanceFundListKind.All ? { kind } : {}),
    ...(queryParams.q ? { search: queryParams.q } : {}),
    page: queryParams.page ?? 1,
    limit: queryParams.limit ?? 20,
    sortBy: parseSortBy(queryParams.sortBy),
    sortOrder: queryParams.sortOrder ?? "asc",
  };
}
